// --- Core ---
import Bottleneck from "bottleneck";
import {isFuture, isPast, parse} from "date-fns";
import {get, remove, set} from "local-storage";
import {createContext} from "react";
import {FormattedMessage} from "react-intl";
import {each, find, map} from "underscore";
// --- SaniQ ---
import config from "config";
import {LS_REMEMBER_TOKEN, SS_AUTH_HEADERS} from "shared/constants/LocalStorage";
import {store} from "src/store";
import {triggerForbidden} from "../modules/auth/actions/Auth";
import {displayErrorbar} from "appActions";
import {APIResponse, handleResponse} from "helpers/Requests";
import {deserializePhysician} from "../models/Physician";
import {JSONAPIContent} from "../models/Helpers";
import {Pagination} from "helpers/interfaces/Pagination";

export {generateUrl} from "./api/generateUrl";
const AbortControllerContext = createContext<AbortController>(null);

/**
 * Definitions for HTTP header keys
 */
const httpHeaders = {
  accept: "Accept",
  acceptLanguage: "Accept-Language",
  accessToken: "access-token",
  client: "client",
  contentType: "Content-Type",
  deprecation: "Deprecation",
  expiry: "expiry",
  link: "Link",
  rememberToken: "Remember-Token",
  retryAfter: "Retry-After",
  sunset: "Sunset",
  uid: "uid",
  xPage: "X-Page",
  xPerPage: "X-Per-Page",
  xTotal: "X-Total",
  xTotalPages: "X-Total-Pages"
};

type HTTPMethod = "DELETE" | "GET" | "PATCH" | "POST";

/**
 * Media types for HTTP requests and responses
 */
const mediaTypes = {
  json: "application/json"
};

/**
 * Parse a given HTTP header as an integer with base 10.
 *
 * @param headers{Headers}
 * @param name{string} name of the HTTP header
 * @param required{boolean} check the header for presence of the entry and that it is an integer
 * @returns {number} the content of the header
 * @throws TypeError
 */
const parseIntHeader = (headers: Headers, name: string, required: boolean = false): number => {
  if (required && !headers.has(name)) {
    throw TypeError(`Headers does not contain required '${name}'`);
  }
  const result = parseInt(headers.get(name), 10);

  if (required && isNaN(result)) {
    throw TypeError(`Header '${name}' is not a required integer`);
  }

  return result;
};

/**
 * Parse the pagination headers from an HTTP header
 *
 * @param headers{Headers}
 * @returns {{total: number, perPage: number, totalPages: number, page: number}}
 */
function parsePagination(headers: Headers): Pagination {
  return {
    total: parseIntHeader(headers, httpHeaders.xTotal, true),
    perPage: parseIntHeader(headers, httpHeaders.xPerPage, true),
    totalPages: parseIntHeader(headers, httpHeaders.xTotalPages, true),
    page: parseIntHeader(headers, httpHeaders.xPage, true)
  };
}

export function findInclude(included, type: string, id: string) {
  return find(included, include => include.type === type && include.id === id);
}

export function findRelationship(included, relationship) {
  if (relationship.data) {
    if (Array.isArray(relationship.data)) {
      return map(relationship.data, (relation) => find(included, include => include.type === relation.type && include.id === relation.id));
    } else {
      return find(included, include => include.type === relationship.data.type && include.id === relationship.data.id);
    }
  }

  return undefined;
}

/**
 * Find the specified relation in the Link header if present
 *
 * @param headers Headers of a HTTP request or response
 * @param relation Relation that should be searched for
 * @returns If found the link specified by the relation
 */
const findLinkHeader = (headers: Headers, relation: string): string | null => {
  let result = null;

  if (headers.has(httpHeaders.link)) {
    const header = headers.get(httpHeaders.link);
    const links = header.split(',');
    const regex = new RegExp(`<([a-z0-9:/%_.]+)>; rel="${relation}"`, "i");

    each(links, (linkEntry) => {
      const found = regex.exec(linkEntry);

      if (found) {
        // eslint-disable-next-line prefer-destructuring
        result = found[1];
      }
    });
  }

  return result;
};

type Deprecations = {
  Deprecation?: {
    data: Date;
    link?: string;
  },
  Sunset?: {
    data: Date;
    link?: string;
  }
};

/**
 * Print the deprecation information specified in deprecations depending on Deprecation and Sunset Time
 *
 * @param deprecations Object describing the deprecations delivered by the response
 * @param request The requested ressource
 */
const printDeprecationInformation = (deprecations: Deprecations, request: string) => {
  if (deprecations[httpHeaders.sunset]) {
    const {date, link} = deprecations[httpHeaders.sunset];

    if (date < new Date()) {
      // eslint-disable-next-line no-console
      console.error(`Accessed sunset ${request}`, link);
    } else {
      // eslint-disable-next-line no-console
      console.warn(`Accessed soon to be sunset ${request}`, link);
    }
  } else if (deprecations[httpHeaders.deprecation]) {
    const {date, link} = deprecations[httpHeaders.deprecation];

    if (date < new Date()) {
      // eslint-disable-next-line no-console
      console.warn(`Accessed deprecated ${request}`, link);
    } else {
      // eslint-disable-next-line no-console
      console.info(`Accessed soon to be deprecated ${request}`, link);
    }
  }
};

/**
 * Check the response headers for the presence of Deprecation or Sunset information and output information on the console if found
 *
 * @param headers Headers of an HTTP request or response
 * @param host The host that was accessed with the request
 * @param path The path that was accessed with the request, query parameters will be hidden
 * @param method The method that was used to access the resource
 */
const checkDeprecationHeaders = (headers: Headers, host: string, path: string, method: string) => {
  // Notify about deprecated and sunset API endpoints
  const deprecations: Deprecations = {};
  let hasDeprecation = false;

  each([httpHeaders.deprecation, httpHeaders.sunset], (deprecationType) => {
    if (headers.has(deprecationType)) {
      const link = findLinkHeader(headers, deprecationType.toLowerCase());

      if (link) {
        deprecations[deprecationType] = {
          date: parse(headers.get(deprecationType), "EEE, dd MMM yyyy HH:mm:ss 'GMT'", new Date()),
          link: link
        };
        hasDeprecation = true;
      }
    }
  });

  if (hasDeprecation) {
    const request = `"${method.toUpperCase()} ${host}${path.split('?')[0]}"`;
    printDeprecationInformation(deprecations, request);
  }
};

const silenceAbortError = (error) => {
  if (!(error instanceof DOMException && error.name === "AbortError")) {
    return Promise.reject(error);
  }
};

type FetchAPIOptions = Partial<{
  headers: HeadersInit;
  ignoreUnauthorized: boolean;
  ignoreForbidden: boolean;
  ignoreNotFound: boolean;
  jobOptions: Bottleneck.JobOptions;
  signal: AbortSignal;
  skipDecoding: boolean;
  skipEncoding: boolean;
  stopAfterNetworkError: boolean;
  withAuthentication: boolean;
}>;

const defaultHeaders: AuthHeaderType = {
  "access-token": "",
  "client": "",
  "uid": ""
};

type AuthHeaderType = {
  "access-token": string;
  "client": string;
  "uid": string;
};

class APIProvider {
  isRemembered: boolean = false;
  locale: string = undefined;
  /**
   * The Date when the session was created/last updated.
   * Is not accurate when loading authentication from session storage
   */
  sessionCreatedAt: Date = undefined;
  sessionExpiryAt: Date = undefined;
  signedIn: boolean = false;
  authHeaders: AuthHeaderType = {...defaultHeaders};

  constructor() {
    this.handleSessionStart = this.handleSessionStart.bind(this);
    this.parseHeaders = this.parseHeaders.bind(this);
    this.fetchPromise = this.fetchPromise.bind(this);
    this.signIn = this.signIn.bind(this);
    this.validateToken = this.validateToken.bind(this);
  }

  handleSessionStart({content}) {
    this.signedIn = true;
    const {rememberToken} = content;

    if (rememberToken) {
      this.isRemembered = true;
      set<string>(LS_REMEMBER_TOKEN, rememberToken);
    }

    return content;
  }

  prepareDataAndHeaders(data, method: HTTPMethod, headers: HeadersInit, options: FetchAPIOptions) {
    let result;

    if (options.skipEncoding) {
      result = data;
    } else {
      result = data ? JSON.stringify(data) : undefined;
      if (method !== "GET") {
        headers[httpHeaders.contentType] = mediaTypes.json;
      }
    }

    if (!options.skipDecoding) {
      headers[httpHeaders.accept] = mediaTypes.json;
    }

    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    return result;
  }

  parseHeaders(headers: Headers) {
    // Extract the authorization tokens from the request. They might change during a session due to token cycling
    if (headers.has(httpHeaders.accessToken) && headers.get(httpHeaders.accessToken)) {
      this.authHeaders[httpHeaders.accessToken] = headers.get(httpHeaders.accessToken);
      this.authHeaders[httpHeaders.client] = headers.get(httpHeaders.client);
      this.authHeaders[httpHeaders.expiry] = headers.get(httpHeaders.expiry);
      this.authHeaders[httpHeaders.uid] = headers.get(httpHeaders.uid);
      this.sessionExpiryAt = new Date(parseInt(headers.get(httpHeaders.expiry)) * 1000);
      this.sessionCreatedAt = new Date();
      window.sessionStorage.setItem(SS_AUTH_HEADERS, JSON.stringify(this.authHeaders));
    }
  }

  fetchPromise(host: string, url: string, method: HTTPMethod, data, options: FetchAPIOptions = {}) {
    return new Promise<HeadersInit>((resolve) => {
      const requestHeaders: HeadersInit = {};

      if (this.locale) {
        requestHeaders[httpHeaders.acceptLanguage] = this.locale;
      }

      if (options.withAuthentication) {
        if (this.signedIn) {
          if (isPast(this.sessionExpiryAt)) {
            this.authHeaders = {...defaultHeaders};
            window.sessionStorage.removeItem(SS_AUTH_HEADERS);
            resolve(
              this.validateToken()
                .then(() => ({...requestHeaders, ...this.authHeaders}))
            );
          } else {
            resolve({...requestHeaders, ...this.authHeaders});
          }
        } else {
          this.authHeaders = {...defaultHeaders};
          resolve(this.validateToken().then(() => ({...requestHeaders, ...this.authHeaders})));
        }
      } else {
        resolve(requestHeaders);
      }
    })
      .then((headers) => ({
        body: this.prepareDataAndHeaders(data, method, headers, options),
        headers: new Headers(headers)
      }))
      .then(({body, headers}) =>
        fetch(host + url, {
          method: method,
          headers,
          body,
          signal: options.signal
        }))
      .then((response) => {
        const {headers} = response;
        this.parseHeaders(headers);
        checkDeprecationHeaders(headers, host, url, method);
        if (response.ok) {
          if (options.skipDecoding) {
            return Promise.resolve({
              content: response,
              status: response.status,
              header: headers
            });
          } else {
            return response.json().then((json) => (
              Promise.resolve({
                content: json,
                status: response.status,
                header: headers
              })
            ));
          }
        } else {
          return response.json()
            .catch(() => (
              Promise.reject({
                content: undefined,
                status: response.status,
                header: headers
              })
            )).then((json) => (
              Promise.reject({
                content: json,
                status: response.status,
                header: headers
              })
            ));
        }
      });
  }

  signIn(data) {
    return customFetch(config.apiUrl, "/web/v1/auth/sign_in", "POST", data, {ignoreForbidden: true, withAuthentication: false}, 0)
      .then(handleResponse(deserializePhysician))
      .then(this.handleSessionStart);
  }

  signInProvider(data) {
    return customFetch(config.apiUrl, '/web/v1/tenant/login_provider', "POST", data, {ignoreForbidden: true, withAuthentication: false}, 0)
      .then(handleResponse(deserializePhysician))
      .then(({content}) => {
        this.signedIn = true;
        return content;
      });
  }

  signOut() {
    return customFetch(config.apiUrl, "/web/v1/auth/sign_out", "DELETE", {}, {
      withAuthentication: true,
      ignoreForbidden: true,
      ignoreNotFound: true
    }, 0)
      .finally(() => {
        this.signedIn = false;
        this.authHeaders = {...defaultHeaders};
        window.sessionStorage.removeItem(SS_AUTH_HEADERS);
        remove(LS_REMEMBER_TOKEN);
      });
  }

  validateToken() {
    //FIXME(Keune): Validate Token is called multiple times, when it is already validating, e.g when switching to a page with multiple requests
    /**
     * The authentication to refresh the token. Either the stored authentication headers or the remember-token
     */
    let authHeaders;
    const rememberToken = get<string>(LS_REMEMBER_TOKEN);
    const storedHeaders = window.sessionStorage.getItem(SS_AUTH_HEADERS);

    if (storedHeaders) {
      try {
        const parsedHeaders = JSON.parse(storedHeaders);
        const sessionExpiryAt = new Date(parseInt(parsedHeaders.expiry) * 1000);

        if (isFuture(sessionExpiryAt)) {
          //NOTE(Keune): When the stored session is not yet expired, use it for re-authentication
          this.sessionExpiryAt = sessionExpiryAt;
          this.sessionCreatedAt = new Date();
          this.authHeaders = authHeaders = parsedHeaders;
        }
      } catch {
        //NOTE(Keune): The stored authentication is not JSON; so it is discarded
        window.sessionStorage.removeItem(SS_AUTH_HEADERS);
      }
    }

    if (!authHeaders && rememberToken) {
      //NOTE(Keune): When there was no stored authentication data, or it was expired, try the remember-token
      authHeaders = {
        [httpHeaders.rememberToken]: rememberToken
      };
    }

    if (!authHeaders) {
      return Promise.reject({data: {}});
    }

    return customFetch(config.apiUrl, "/web/v1/auth/validate_token", "GET", undefined, {
      headers: authHeaders,
      ignoreForbidden: true
    }, 0)
      .then(handleResponse(deserializePhysician))
      .then(this.handleSessionStart)
      .catch(() => {
        this.signedIn = false;
        this.authHeaders = {...defaultHeaders};
        window.sessionStorage.removeItem(SS_AUTH_HEADERS);
        remove(LS_REMEMBER_TOKEN);
        return Promise.reject({data: {}});
      });
  }
}

export const API = new APIProvider();
const limiter = new Bottleneck({maxConcurrent: 10});

async function customFetch(host: string, url: string, method: HTTPMethod, data, options: FetchAPIOptions, maxRetries = 1, tries = 1) {
  return limiter.schedule(options.jobOptions || {}, API.fetchPromise, host, url, method, data, options).catch((err) => {
    const timeout = err.headers?.get(httpHeaders.retryAfter) * 1000 || tries * 3000;

    if (err instanceof TypeError) {
      if (options.stopAfterNetworkError) {
        return Promise.reject(err);
      }

      displayErrorbar(
        <FormattedMessage
          id="api.typeError"
          defaultMessage="Connection lost. Trying again in {seconds} seconds"
          values={{seconds: timeout / 1000}}
        />, {autoHideDuration: timeout});

      return new Promise(resolve => setTimeout(resolve, timeout))
        .then(() => customFetch(host, url, method, data, options, maxRetries, tries + 1));
    } else if (err instanceof DOMException && err.name === "AbortError") {
      return Promise.reject(err);
    }

    switch (err.status) {
      case 401: // Unauthorized
        if (!options.ignoreForbidden && !options.ignoreUnauthorized) {
          store.dispatch(triggerForbidden());
        }

        return Promise.reject(err);
      case 403: // Forbidden
        if (!options.ignoreForbidden) {
          store.dispatch(triggerForbidden());
        }

        return Promise.reject(err);
      case 404: // Not Found
        if (!options.ignoreNotFound) {
          displayErrorbar(<FormattedMessage id="api.404" defaultMessage="Error 404. Page not found."/>);
        }

        return Promise.reject(err);
      case 406: // Not Acceptable
        if (!options.ignoreForbidden) {
          store.dispatch(triggerForbidden());
        }

        return Promise.reject(err);
      case 422: // Unprocessable Content
        return Promise.reject(err);
      case 500: // Internal Server Error
        return Promise.reject(err);
      default:
        if (err.status !== 429 && maxRetries < tries) {
          displayErrorbar(<FormattedMessage id="api.maxRetries" defaultMessage="Connection error. Maximum retries reached."/>);
          return Promise.reject(err);
        }

        displayErrorbar(
          <FormattedMessage
            id="api.retry"
            defaultMessage="Connection error. Trying again ({tries}/{maxRetries})"
            values={{tries: tries, maxRetries: maxRetries}}
          />, {autoHideDuration: timeout});

        return new Promise(resolve => setTimeout(resolve, timeout))
          .then(() => customFetch(host, url, method, data, options, maxRetries, tries + 1));
    }
  });
}

function fetchQurasoftApi(url: string, method: HTTPMethod = "GET", options: FetchAPIOptions = {}, maxRetryCount = 3) {
  return customFetch(config.qurasoftApiUrl, url, method, null, {
    ...options,
    withAuthentication: false,
    headers: {"Authorization": `bearer ${config.qurasoftApiKey}`}
  }, maxRetryCount);
}

function fetchAPI<T = JSONAPIContent>(
  url: string,
  method: HTTPMethod = "GET",
  data = null,
  options: FetchAPIOptions = {},
  maxRetryCount: number = 3
): Promise<APIResponse<T>> {
  return customFetch(config.apiUrl, url, method, data, {withAuthentication: true, ...options}, maxRetryCount);
}

function fetchPollen(url: string, method: HTTPMethod = "GET", data = null, options: FetchAPIOptions = {}, maxRetryCount = 3) {
  return customFetch(`${config.appApiUrl}/v1`, url, method, data, options, maxRetryCount);
}

function getAPIBlob(url: string, maxRetryCount?: number) {
  return fetchAPI<Response>(url, "GET", undefined,  {skipDecoding: true}, maxRetryCount)
    .then(({content}) => content.blob());
}

export {
  AbortControllerContext,
  fetchAPI,
  FetchAPIOptions,
  fetchPollen,
  fetchQurasoftApi,
  getAPIBlob,
  parsePagination,
  silenceAbortError
};
